import { motion } from "framer-motion";
import { CheckCircle2, Stethoscope, CalendarCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const DETAILED = [
  {
    title: "Ear Infections — Otitis Media & Otitis Externa",
    blurb: "Ear pain can have several causes, including an infection of the outer ear, a middle-ear infection or another ear problem. Our pharmacist can discuss your symptoms and examine the ear where appropriate. Depending on the findings and your individual circumstances, we may provide advice or treatment within our scope, or refer you to a doctor for further assessment.",
  },
  {
    title: "Shingles",
    blurb: "Shingles commonly causes pain, tingling or sensitivity followed by a blistering rash on one side of the body. Early assessment is important because treatment may be most effective when started promptly. Our pharmacist can assess your symptoms, determine whether pharmacist treatment is appropriate and provide advice about symptom relief, reducing transmission and when further medical care is required.",
  },
  {
    title: "Mild to Moderate Acne",
    blurb: "Our pharmacist can assess your acne, discuss your skin-care routine, previous treatments and factors that may be affecting your skin. Where appropriate, we can recommend a personalised treatment and skin-care plan, explain how to use your products correctly and arrange follow-up. More severe, persistent or scarring acne will be referred for medical care.",
  },
  {
    title: "Minor Wound Management",
    blurb: "Our minor wound service provides assessment and practical care for suitable cuts, grazes, skin tears and other uncomplicated wounds. We can examine and clean the wound, help select an appropriate dressing and explain how to care for it at home. We will also look for signs of infection or delayed healing and recommend follow-up or medical care when needed.",
  },
  {
    title: "Impetigo (School Sores)",
    blurb: "Impetigo is a contagious skin infection that often causes red sores, blisters or honey-coloured crusts. Our pharmacist can assess the affected skin, discuss how the infection is spreading and determine whether treatment through the pharmacy is suitable. We can also explain hygiene measures that help protect other household or school members and arrange medical referral when necessary.",
  },
  {
    title: "Smoking Cessation",
    blurb: "Stopping smoking can be difficult, but you do not have to do it alone. Our pharmacist can help you understand your smoking patterns, triggers and nicotine dependence, then develop a practical quit plan suited to you. Support may include behavioural strategies, advice about suitable nicotine-replacement options, progress reviews and referral for additional treatment when required.",
  },
  {
    title: "Weight Management",
    blurb: "Our weight-management service offers respectful, non-judgmental and individual support. Your consultation may include a discussion of your goals, health history, medicines, eating patterns, physical activity and factors affecting your progress. Together, we can develop a realistic plan, monitor your progress and coordinate with your doctor or other healthcare professionals when additional support is appropriate.",
  },
];

const ALSO_AVAILABLE = [
  "Hormonal contraception",
  "Eczema & atopic dermatitis",
  "Mild plaque psoriasis flare-ups",
  "Reflux & heartburn (GORD)",
  "Acute nausea & vomiting",
  "Hay fever & allergic rhinitis",
  "Musculoskeletal pain & inflammation",
  "Travel health",
  "Oral health screening & fluoride",
];

export default function ScopeSection() {
  return (
    <section id="scope" data-testid="scope-section" className="scroll-mt-24 bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="mb-10" data-testid="scope-coming-soon-wrap">
          <span
            className="inline-flex items-center gap-2 rounded-full bg-brand-pink px-5 py-2 font-heading text-sm font-bold uppercase tracking-widest text-white shadow-[0_8px_24px_rgba(229,0,126,0.3)]"
            data-testid="scope-coming-soon"
          >
            <Sparkles className="h-4 w-4" /> Coming soon
          </span>
        </div>

        <div className="grid gap-14 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-greendark" data-testid="scope-eyebrow">
              Expanded Scope of Practice
            </p>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight sm:text-4xl" data-testid="scope-heading">
              See the pharmacist first for everyday conditions
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              Under community pharmacy expanded scope of practice, our trained pharmacists will be able to assess and
              manage a growing list of common acute conditions.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                asChild
                data-testid="scope-book-consult-btn"
                className="rounded-full bg-brand-green px-6 font-heading font-semibold text-white hover:bg-brand-greendark"
              >
                <a href="#book"><CalendarCheck className="mr-2 h-4 w-4" /> Book a consult</a>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="scope-call-btn"
                className="rounded-full border-2 border-foreground/15 px-6 font-heading font-semibold hover:border-brand-green hover:text-brand-greendark"
              >
                <a href="tel:+61897572224">Call 9757 2224</a>
              </Button>
            </div>
            <div className="mt-10 overflow-hidden rounded-2xl">
              <div className="flex items-center gap-3 rounded-2xl bg-brand-cream p-5">
                <Stethoscope className="h-8 w-8 shrink-0 text-brand-pink" />
                <p className="text-sm text-muted-foreground">
                  A consultation fee may apply. Our pharmacist will always refer you to a GP if your condition needs it.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="space-y-3" data-testid="scope-detailed-list">
              {DETAILED.map((d, i) => (
                <motion.div
                  key={d.title}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.35, delay: (i % 4) * 0.05 }}
                >
                  <AccordionItem
                    value={d.title}
                    className="overflow-hidden rounded-xl border border-border/60 bg-brand-cream transition-colors duration-200 data-[state=open]:border-brand-green/50"
                    data-testid={`scope-box-${i}`}
                  >
                    <AccordionTrigger
                      className="px-5 py-4 text-left hover:no-underline"
                      data-testid={`scope-toggle-${i}`}
                    >
                      <span className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 shrink-0 text-brand-green" />
                        <span className="font-heading text-sm font-bold">{d.title}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="px-5 pb-5 pl-13" data-testid={`scope-content-${i}`}>
                      <p className="pl-8 text-sm leading-relaxed text-muted-foreground">{d.blurb}</p>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>

            <p className="mt-8 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Also ask us about</p>
            <ul className="mt-3 flex flex-wrap gap-2" data-testid="scope-also-list">
              {ALSO_AVAILABLE.map((c, i) => (
                <li
                  key={c}
                  data-testid={`scope-also-${i}`}
                  className="rounded-full border border-border/60 bg-brand-cream px-4 py-2 text-xs font-medium text-foreground/80"
                >
                  {c}
                </li>
              ))}
            </ul>

            <p className="mt-8 rounded-xl border border-border/60 bg-white p-4 text-xs leading-relaxed text-muted-foreground" data-testid="scope-disclaimer">
              <strong className="text-foreground">Important information:</strong> Each consultation is individual. The
              service provided will depend on your symptoms, medical history, medicines, age, clinical eligibility and
              the pharmacist's assessment. Treatment cannot be guaranteed. If your condition is severe, uncertain or
              outside the pharmacist's scope, we will recommend the most appropriate next step or refer you to another
              healthcare professional.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
